import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { doc, collection, getDocs, updateDoc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { createUserWithEmailAndPassword, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './AccountSettings.scss';
import { API_ENDPOINTS } from '../../config/api';

interface User {
    email: string;
    isAdmin: boolean;
    id: string;
}

const AccountSettings: React.FC = () => {
    const { currentUser, logout, isAdmin } = useAuth();
    const [users, setUsers] = useState<User[]>([]);
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserPassword, setNewUserPassword] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showNewUserPassword, setShowNewUserPassword] = useState(false);

    useEffect(() => {
        if (currentUser) {
            setupAdminUser();
        }
    }, [currentUser]);

    async function setupAdminUser() {
        if (!currentUser) return;

        try {
            const userDocRef = doc(db, 'users', currentUser.uid);

            if (currentUser.email === 'jack@sitereviver.com') {
                await setDoc(userDocRef, {
                    email: currentUser.email,
                    isAdmin: true,
                    createdAt: new Date().toISOString(),
                    uid: currentUser.uid
                });

                const docSnap = await getDoc(userDocRef);
                if (docSnap.exists() && docSnap.data().isAdmin) {
                    console.log('Admin status confirmed');
                } else {
                    console.error('Admin document not created properly');
                }
            }
        } catch (error) {
            console.error('Error setting up admin user:', error);
        }
    }

    useEffect(() => {
        if (isAdmin) {
            fetchUsers();
        }
    }, [isAdmin]);

    async function fetchUsers() {
        try {
            const usersCollection = collection(db, 'users');
            const usersSnapshot = await getDocs(usersCollection);
            const usersList = usersSnapshot.docs.map(doc => ({
                ...doc.data(),
                id: doc.id,
                email: doc.data().email,
                isAdmin: doc.data().isAdmin || false
            })) as User[];
            setUsers(usersList);
        } catch (error) {
            console.error('Error fetching users:', error);
            setError('Failed to fetch users');
        }
    }

    async function handlePasswordChange(e: React.FormEvent) {
        e.preventDefault();
        if (!currentUser) {
            return setError('No user logged in');
        }
        if (newPassword !== confirmPassword) {
            return setError('Passwords do not match');
        }
        try {
            setError('');
            setSuccess('');
            setLoading(true);

            const credential = EmailAuthProvider.credential(
                currentUser.email!,
                currentPassword
            );
            await reauthenticateWithCredential(currentUser, credential);
            await updatePassword(currentUser, newPassword);

            setSuccess('Password updated successfully');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (error: any) {
            console.error('Error updating password:', error);
            if (error.code === 'auth/wrong-password') {
                setError('Current password is incorrect');
            } else {
                setError(error.message || 'Failed to update password');
            }
        } finally {
            setLoading(false);
        }
    }

    async function handleCreateUser(e: React.FormEvent) {
        e.preventDefault();
        if (!newUserEmail || !newUserPassword) {
            return setError('Please fill in all fields');
        }
        try {
            setError('');
            setSuccess('');
            setLoading(true);

            const idToken = await auth.currentUser?.getIdToken();

            const response = await fetch(API_ENDPOINTS.USERS, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                },
                body: JSON.stringify({
                    email: newUserEmail,
                    password: newUserPassword
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to create user');
            }

            const data = await response.json();

            setSuccess('User created successfully');
            setNewUserEmail('');
            setNewUserPassword('');
            await fetchUsers();
        } catch (error: any) {
            console.error('Error creating user:', error);
            setError(error.message || 'Failed to create user');
        } finally {
            setLoading(false);
        }
    }

    async function handleLogout() {
        try {
            await logout();
        } catch (error) {
            setError('Failed to log out');
        }
    }

    return (
        <div className="account-settings-container">
            <div className="account-settings-content">
                <h1>Account Settings</h1>

                {error && <div className="error-message">{error}</div>}
                {success && <div className="success-message">{success}</div>}

                <section className="settings-section">
                    <h2>Your Account</h2>
                    <div className="user-info">
                        <p><strong>Email:</strong> {currentUser?.email}</p>
                        <p><strong>Role:</strong> {isAdmin ? 'Administrator' : 'Standard User'}</p>
                    </div>
                </section>

                <section className="settings-section">
                    <h2>Change Password</h2>
                    <form onSubmit={handlePasswordChange}>
                        <div className="form-group">
                            <label>Current Password</label>
                            <div className="password-input">
                                <input
                                    type={showCurrentPassword ? "text" : "password"}
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                    className="toggle-password"
                                >
                                    {showCurrentPassword ? "Hide" : "Show"}
                                </button>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>New Password</label>
                            <div className="password-input">
                                <input
                                    type={showNewPassword ? "text" : "password"}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    className="toggle-password"
                                >
                                    {showNewPassword ? "Hide" : "Show"}
                                </button>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Confirm New Password</label>
                            <div className="password-input">
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="toggle-password"
                                >
                                    {showConfirmPassword ? "Hide" : "Show"}
                                </button>
                            </div>
                        </div>

                        <button type="submit" disabled={loading} className="submit-button">
                            {loading ? 'Updating...' : 'Update Password'}
                        </button>
                    </form>
                </section>

                {isAdmin && (
                    <section className="settings-section">
                        <h2>Create New User</h2>
                        <form onSubmit={handleCreateUser}>
                            <div className="form-group">
                                <label>Email</label>
                                <input
                                    type="email"
                                    value={newUserEmail}
                                    onChange={(e) => setNewUserEmail(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Password</label>
                                <div className="password-input">
                                    <input
                                        type={showNewUserPassword ? "text" : "password"}
                                        value={newUserPassword}
                                        onChange={(e) => setNewUserPassword(e.target.value)}
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNewUserPassword(!showNewUserPassword)}
                                        className="toggle-password"
                                    >
                                        {showNewUserPassword ? "Hide" : "Show"}
                                    </button>
                                </div>
                            </div>

                            <button type="submit" disabled={loading} className="submit-button">
                                {loading ? 'Creating...' : 'Create User'}
                            </button>
                        </form>
                    </section>
                )}

                {isAdmin && (
                    <section className="settings-section">
                        <h2>Manage Users</h2>
                        <div className="users-list">
                            {users.map(user => (
                                <div key={user.id} className="user-item">
                                    <div className="user-details">
                                        <span>{user.email}</span>
                                        <span className={`user-role ${user.isAdmin ? 'admin' : 'user'}`}>
                                            {user.isAdmin ? 'Admin' : 'User'}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                <section className="settings-section">
                    <button onClick={handleLogout} className="logout-button">
                        Logout
                    </button>
                </section>
            </div>
        </div>
    );
};

export default AccountSettings; 